import { api } from "@/lib/api";
import {
  PaymentDto,
  paymentDtoListSchema,
  PaymentDtoSchema,
  paymentFromDto,
} from "@/models";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useToast } from "../use-toast";

export function getPaymentsQueryOptions(reservationId: string) {
  return queryOptions({
    queryKey: ["payments", reservationId],
    queryFn: async () => {
      const result: Array<PaymentDto> = [];
      let page = 0;
      let hasNextPage = true;
      while (hasNextPage) {
        page += 1;
        const res = await api.get("/api/payments", paymentDtoListSchema, {
          query: {
            page: page.toString(),
          },
          filters: {
            ReservationId: Number.parseInt(reservationId),
          },
        });
        result.push(...res.Data);
        hasNextPage = res.PageInfo.HasNextPage;
      }
      return result.map(paymentFromDto);
    },
  });
}
export function useGetPayments<TERROR extends Error>(
  reservationId: string,
  opts?: {
    onError?: (error: TERROR) => void;
    enabled?: boolean;
  },
) {
  const { toast } = useToast();

  const q = useQuery({
    ...getPaymentsQueryOptions(reservationId),
    enabled: opts?.enabled,
  });
  useEffect(() => {
    if (!q.error) return;
    toast({
      title: "toast.getPaymentsError",
      variant: "destructive",
    });
    opts?.onError?.(q.error as TERROR);
  }, [opts, q.error, toast]);
  return q;
}

export function getPaymentQueryOptions(id: string) {
  return queryOptions({
    queryKey: ["payment-details", id],
    queryFn: () =>
      api
        .get("/api/payments/$id", PaymentDtoSchema, {
          pathReplace: { $id: id },
        })
        .then(paymentFromDto),
  });
}

export function useGetPayment<TERROR extends Error>(
  id: string,
  opts?: { onError?: (error: TERROR) => void; enabled?: boolean },
) {
  const { toast } = useToast();
  const q = useQuery({
    ...getPaymentQueryOptions(id),
    enabled: opts?.enabled,
  });
  useEffect(() => {
    if (!q.error) return;
    toast({
      title: "toast.getPaymentDetailError",
      variant: "destructive",
    });
    opts?.onError?.(q.error as TERROR);
  }, [opts, q.error, toast]);
  return q;
}
